import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import FinishScreen from './FinishScreen';
import stepPages from './stepPages';
import StepPlaceholder from './StepPlaceholder';
import StepRail from './StepRail';
import { StepFrameContext } from './steps/StepShell';

import { useToast } from 'Hooks/useToast';

const FINISH = 'finish';

/**
 * The wizard container: the step rail and progress bar on one side, the
 * current step's panel on the other. Step pages ship separately — this shell
 * owns navigation, deferral and the derived step statuses around them.
 */
export default function SetupWizard({ initialSteps, initialProgress, initialCurrentStep, initialContinueStep, initialUrls }) {
    const [steps, setSteps] = useState(initialSteps);
    const [progress, setProgress] = useState(initialProgress);
    const [currentStep, setCurrentStep] = useState(initialCurrentStep || FINISH);
    // Read by handleRefresh when its response lands, which can be after the
    // user has already moved on.
    const currentStepRef = useRef(currentStep);
    currentStepRef.current = currentStep;
    // Where the finish screen's "Continue setup" goes; resolved server-side.
    const [continueStep, setContinueStep] = useState(initialContinueStep || null);
    // Held in state, not read from props: renaming the project on the Project
    // basics page re-slugs the Event, and the slug is its route key — every URL
    // baked into the mount attributes goes dead at that moment. Each save
    // returns a fresh set.
    const [urls, setUrls] = useState(initialUrls);
    const [deferring, setDeferring] = useState(false);
    const [saving, setSaving] = useState(false);
    const toast = useToast();

    // "Finish required setup" on this step always leads to the finish screen,
    // whatever is still outstanding.
    const lastRequiredKey = useMemo(() => steps.filter((step) => step.group === 'required').slice(-1)[0]?.key ?? null, [steps]);

    const pushRoute = useCallback((stepList, key) => {
        const target = stepList.find((step) => step.key === key);
        if (target) {
            window.history.pushState({}, '', target.route);
        }
    }, []);

    const goTo = useCallback(
        (key) => {
            setCurrentStep(key);
            pushRoute(steps, key);
        },
        [pushRoute, steps],
    );

    // Browser Back/Forward change the URL via history alone (pushRoute never
    // registers a popstate listener), so without this the address bar and the
    // rendered panel drift apart.
    useEffect(() => {
        const onPopState = () => {
            // Matched on the step segment alone, not the whole pathname: history
            // entries created before a rename still carry the old slug, and
            // comparing full paths would match nothing and drop the user on the
            // finish screen holding a URL that no longer resolves.
            const key = window.location.pathname.match(/\/setup-wizard\/([^/]+)\/?$/)?.[1] ?? null;
            const match = key ? steps.find((step) => step.key === key) : null;

            setCurrentStep(match ? match.key : FINISH);

            // The entry we just landed on may predate a rename. Rewrite it to the
            // current slug so a refresh from here does not 404.
            if (match) {
                try {
                    const current = new URL(match.route, window.location.origin).pathname;
                    if (current !== window.location.pathname) {
                        window.history.replaceState({}, '', match.route);
                    }
                } catch {
                    /* a malformed route is not worth breaking navigation over */
                }
            }
        };

        window.addEventListener('popstate', onPopState);

        return () => window.removeEventListener('popstate', onPopState);
    }, [steps]);

    const applyPayload = useCallback((data) => {
        setSteps(data.steps);
        setProgress(data.progress);
        setContinueStep(data.continue_step ?? null);

        if (data.urls) {
            setUrls(data.urls);
        }
    }, []);

    /**
     * Moves to whatever the server says is outstanding, reading the step list
     * out of the response rather than from state — state still holds the
     * pre-request snapshot at this point, so navigating from it can land on a
     * step this very response marked complete.
     */
    const advanceFrom = useCallback(
        (data) => {
            const next = data.current_step || FINISH;
            setCurrentStep(next);
            pushRoute(data.steps, next);
        },
        [pushRoute],
    );

    /**
     * indexUrl is passed in when the caller holds a fresher payload than
     * state does, since a rename re-slugs every URL.
     */
    const goFinish = useCallback(
        (indexUrl) => {
            setCurrentStep(FINISH);
            window.history.pushState({}, '', indexUrl || urls.index);
        },
        [urls.index],
    );

    const handleDefer = useCallback(
        (key) => {
            setDeferring(true);

            axios
                .post(urls.defer_template.replace('__STEP__', key))
                .then(({ data }) => {
                    applyPayload(data);
                    advanceFrom(data);
                })
                .catch(() => toast.error($t('Something went wrong. Please try again.')))
                .finally(() => setDeferring(false));
        },
        [advanceFrom, applyPayload, urls.defer_template, toast],
    );

    /**
     * Records the current step as saved before moving on — the only
     * completion signal a toggle-only page (Modules, Functionality,
     * Commitments) has, since every toggle always holds a value. Harmless for
     * data-derived steps: their status ignores it entirely.
     *
     * The last visible step's button is "Finish setup", so it leads to the
     * finish screen whatever is still outstanding.
     */
    const handleNext = useCallback(() => {
        setSaving(true);
        const lastVisibleKey = steps.filter((step) => step.status !== 'not_applicable').slice(-1)[0]?.key ?? null;

        axios
            .post(urls.save_template.replace('__STEP__', currentStep))
            .then(({ data }) => {
                applyPayload(data);

                if (currentStep === lastRequiredKey || currentStep === lastVisibleKey) {
                    goFinish(data.urls?.index);
                } else {
                    advanceFrom(data);
                }
            })
            .catch(() => toast.error($t('Something went wrong. Please try again.')))
            .finally(() => setSaving(false));
    }, [advanceFrom, applyPayload, currentStep, goFinish, lastRequiredKey, steps, urls.save_template, toast]);

    /**
     * A step page has persisted its own data and marked itself saved in one
     * request, so the response is the same payload handleNext would have got.
     * Reusing this pair keeps navigation in the shell.
     *
     * replaceState rather than pushRoute: the project may have been renamed
     * and re-slugged, so the address bar is holding a URL that no longer
     * resolves. Rewriting it in place avoids leaving a dead entry in history.
     */
    const handleSaved = useCallback(
        (data) => {
            applyPayload(data);

            const stillHere = data.steps.find((step) => step.key === currentStep);
            if (stillHere) {
                window.history.replaceState({}, '', stillHere.route);
            }

            advanceFrom(data);
        },
        [advanceFrom, applyPayload, currentStep],
    );

    /**
     * A step page changed data that its own status — or whether it applies at
     * all — is derived from, without leaving the page. Re-reads the rail, and
     * moves on only when the current step has just become not applicable.
     */
    const handleRefresh = useCallback(() => {
        const requestedFrom = currentStepRef.current;

        axios
            .get(urls.index)
            .then(({ data }) => {
                applyPayload(data);

                // Only move on if the user is still on the step that asked.
                if (currentStepRef.current !== requestedFrom) {
                    return;
                }

                const here = data.steps.find((step) => step.key === requestedFrom);
                if (here && here.status === 'not_applicable') {
                    advanceFrom(data);
                }
            })
            .catch(() => toast.error($t('Something went wrong. Please try again.')));
    }, [advanceFrom, applyPayload, urls.index, toast]);

    const activeStep = steps.find((step) => step.key === currentStep) ?? null;
    const StepPage = (activeStep && stepPages[activeStep.key]) || StepPlaceholder;
    // The nearest earlier step that has a page; not-applicable steps are greyed
    // out in the rail and have none.
    const previousStep = activeStep
        ? steps
              .slice(
                  0,
                  steps.findIndex((step) => step.key === activeStep.key),
              )
              .reverse()
              .find((step) => step.status !== 'not_applicable')
        : null;
    const onBack = previousStep ? () => goTo(previousStep.key) : null;
    // "Step N of M" counts the visible steps only, as the rail's progress does.
    const visibleSteps = steps.filter((step) => step.status !== 'not_applicable');
    const frame = { onBack, position: visibleSteps.findIndex((step) => step.key === activeStep?.key) + 1, total: visibleSteps.length };
    const stepUrls = activeStep ? urls.steps?.[activeStep.key] : null;

    return (
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[18rem_minmax(0,1fr)] tw-gap-6 tw-p-4">
            <StepRail steps={steps} progress={progress} currentStep={currentStep} onSelect={goTo} />

            {activeStep ? (
                <StepFrameContext.Provider value={frame}>
                    <StepPage
                        key={activeStep.key}
                        step={activeStep}
                        urls={stepUrls}
                        deferring={deferring}
                        saving={saving}
                        onDefer={handleDefer}
                        onNext={handleNext}
                        onSaved={handleSaved}
                        onRefresh={handleRefresh}
                        onStatusChange={applyPayload}
                        onGoTo={goTo}
                    />
                </StepFrameContext.Provider>
            ) : (
                <FinishScreen steps={steps} projectUrl={urls.project} continueStep={continueStep} onSelect={goTo} />
            )}
        </div>
    );
}

if (document.getElementById('SetupWizard')) {
    var rootEl = document.getElementById('SetupWizard');
    ReactDOM.render(
        <App>
            <SetupWizard
                initialSteps={JSON.parse(rootEl.getAttribute('data-steps'))}
                initialProgress={JSON.parse(rootEl.getAttribute('data-progress'))}
                initialCurrentStep={rootEl.getAttribute('data-current-step')}
                initialContinueStep={rootEl.getAttribute('data-continue-step')}
                initialUrls={JSON.parse(rootEl.getAttribute('data-urls'))}
            />
        </App>,
        rootEl,
    );
}
